import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { contains } from 'ramda';
import { withAsyncActions, withSagas, toSuccess } from 'react-async-client';
import { Button, Row } from 'antd';
import Translate from 'react-translate-component';
import cx from 'classnames';
import { takeEvery } from 'redux-saga/effects';
import ReactToPrint from 'react-to-print';

import {
    TEXT,
    IMAGE,
    FRAME,
    SCRIPT,
    XMHT,
    XMLFORM,
    XMLTEST,
    XMLRESUME,
    XMLVCV,
} from 'constants/documentTypes';

import DocumentText from './preview/DocumentText';
import DocumentImage from './preview/DocumentImage';
import DocumentFrame from './preview/DocumentFrame';
import DocumentXMHT from './preview/DocumentXMHT';
import DocumentScript from './preview/DocumentScript';
import DocumentXMLForm from './preview/DocumentXMLForm';
import DocumentXMLTest from './preview/DocumentXMLTest';
import DocumentXMLResume from './preview/DocumentXMLResume';
import DocumentXMLVCV from './preview/DocumentXMLVCV';

import { fullAutoOptions } from 'constants/asyncClient';
import { PUT_DOCUMENT_TEXT, PUT_DOCUMENT_XML } from 'constants/actionTypes';
import { getDocument, getDocumentInfo, getMetroDocument } from 'actions/asyncActions';
import ModalLoader from 'components/modals/ModalLoader';
import EmptyDocument from './preview/EmptyDocument';
import DocumentEditScript from './edit/DocumentEditScript';
import DocumentEditText from './edit/DocumentEditText';
import DocumentEditXML from './edit/DocumentEditXML';
import DocumentPreviewFooter from './DocumentPreviewFooter';
import { notEmpty } from 'utils/ramdaAdditions';
import { canPrint } from 'utils/print';
import CollapsibleModalContainer from '../../modals/CollapsibleModalContainer';

const previewComponents = {
    [TEXT]: DocumentText,
    [IMAGE]: DocumentImage,
    [FRAME]: DocumentFrame,
    [SCRIPT]: DocumentScript,
    [XMHT]: DocumentXMHT,
    [XMLFORM]: DocumentXMLForm,
    [XMLTEST]: DocumentXMLTest,
    [XMLRESUME]: DocumentXMLResume,
    [XMLVCV]: DocumentXMLVCV,
};

const getDocumentTypes = [
    IMAGE,
    XMHT
];

const editComponents = {
    // [SCRIPT]: DocumentEditScript,
    [TEXT]: DocumentEditText,
    [XMLFORM]: DocumentEditXML,
};

const editComponentsEvents = {
    [SCRIPT]: DocumentEditScript,
    [TEXT]: DocumentEditText,
    [XMLFORM]: DocumentEditXML,
};

const editableDocumentTypes = [
    SCRIPT,
    TEXT,
    XMLFORM
];

@withAsyncActions(({ document, documentPreviewType }) => {
    const { id, place, metro } = document;

    return contains(documentPreviewType, getDocumentTypes) ? {} : ({
        getDocumentInfo: getDocumentInfo
            .withParams({ id, place })
            .withPayload(({ document }) => document)
            .withOptions(metro ? {} : fullAutoOptions),
        getDocument: (metro ? getMetroDocument : getDocument)
            .withParams({ id, place, request: metro })
            .withPayload(({ document }) => document)
            .withOptions(fullAutoOptions)
    });
})
@withSagas([function* (getProps) {
    yield takeEvery([toSuccess(PUT_DOCUMENT_TEXT), toSuccess(PUT_DOCUMENT_XML)], (action) => {
        const props = getProps();

        if (action.payload.id === props.document.id) {
            props.getDocument.refresh();
            props.getDocumentInfo.refresh();
        }
    });
}])
export default class DocumentPreview extends Component {
    constructor(props) {
        super(props);
        this.documentContainer = React.createRef();
        this.print = React.createRef();
    }

    static propTypes = {
        isModal: PropTypes.bool,
        showFooter: PropTypes.bool,
        close: PropTypes.func,
        getDocument: PropTypes.object,
        getDocumentInfo: PropTypes.object,
        document: PropTypes.object.isRequired,
        event: PropTypes.object.isRequired,
        documentPreviewType: PropTypes.string.isRequired
    }

    state = {
        editForm: false,
    }

    toggleEdit = () => {
        this.setState({
            editForm: !this.state.editForm,
        })
    }

    render() {
        const { editForm } = this.state;
        const { isModal, showFooter, document, event, close, getDocument = {}, getDocumentInfo = {}, documentPreviewType } = this.props;
        const { data, meta, refresh } = getDocument;
        const { data : info = {} } = getDocumentInfo;

        const isEmptyDocument = !notEmpty(data) && meta;
        const isSuccess = (!meta || meta.success);
        const PreviewComponent = previewComponents[documentPreviewType];
        const editable = info.editable && contains(documentPreviewType, editableDocumentTypes);
        const scriptEditing = editForm && documentPreviewType === SCRIPT;

        const DocumentEditForm = editable && (event ? editComponentsEvents : editComponents)[documentPreviewType];
        const footer = !editForm && (
            <DocumentPreviewFooter
                document={document}
                documentPreviewType={documentPreviewType}
                close={close}
                edit={editable && DocumentEditForm && this.toggleEdit}
                isSuccess={isSuccess}
                print={this.print}
            />
        );

        const preview = (
            <Fragment>
                { isSuccess && isEmptyDocument && <EmptyDocument /> }
                { isSuccess && !isEmptyDocument && (
                    !editForm ? (
                        <Fragment>
                            <PreviewComponent
                                ref={this.documentContainer}
                                documentPreviewType={documentPreviewType}
                                document={document}
                                info={info}
                                body={data}
                            />
                            { showFooter && (
                                <div className="m-t-20">
                                    {footer}
                                </div>
                            )}
                        </Fragment>
                    ) : (
                        <DocumentEditForm
                            document={document}
                            event={event}
                            body={data}
                            close={this.toggleEdit}
                            className={cx({ 'panel-view': !showFooter })}
                            footer={handleSubmit => (
                                <Row>
                                    { handleSubmit && (
                                        <Button onClick={handleSubmit} type='primary'>
                                            <Translate content="events.save" />
                                        </Button>
                                    )}
                                    <Button className="card-button close-button" onClick={this.toggleEdit}>
                                        <Translate content="events.cancel" />
                                    </Button>
                                </Row>
                            )}
                        />
                    )
                )}
            </Fragment>
        );

        return isModal ? (
            <CollapsibleModalContainer
                title={document.name}
                footer={footer}
                meta={meta}
                className={cx({ 'edit-script-modal': scriptEditing })}>
                { preview }
                { canPrint(documentPreviewType) && (
                    <ReactToPrint
                        ref={this.print}
                        trigger={() => <span />}
                        content={() => this.documentContainer.current}
                        bodyClass='ant-modal'
                    />
                )}
            </CollapsibleModalContainer>
        ) : (
            <ModalLoader meta={meta || {}} refresh={refresh}>
                { preview }
            </ModalLoader>
        );
    }
}
